import { AmountDisplay, amountString } from "@/app/shared/ui/amount-display";
import { useDemoModeStore } from "@/store/demoMode";
import { ChevronRight } from "lucide-react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "@/lib/date.utils";
import { cn } from "@/lib/utils";
import type { CreditCardCategorySpend, CreditCardOverview } from "@/types";

interface CategoryBreakdownProps {
  overview: CreditCardOverview | undefined;
  isLoading: boolean;
  month: string;
}

/**
 * Category-wise spend across ALL cards for the billing month, grouped by
 * category group with section labels (mirrors the Budgets page layout).
 * Rows deep-link into the hub's card register filtered by category + the
 * union window of the period.
 */
export function CategoryBreakdown({ overview, isLoading }: CategoryBreakdownProps) {
  const { enabled: isDemoMode } = useDemoModeStore();
  const navigate = useNavigate();

  const groups = useMemo(() => {
    const categories = overview?.categories ?? [];
    const byGroup = new Map<string, { label: string; items: CreditCardCategorySpend[] }>();
    for (const cat of categories) {
      const key = cat.categoryGroupId ? String(cat.categoryGroupId) : "ungrouped";
      const label = cat.categoryGroupName ?? "Other";
      if (!byGroup.has(key)) byGroup.set(key, { label, items: [] });
      byGroup.get(key)?.items.push(cat);
    }
    return [...byGroup.values()].sort(
      (a, b) =>
        b.items.reduce((s, i) => s + i.amount, 0) - a.items.reduce((s, i) => s + i.amount, 0),
    );
  }, [overview]);

  const { from, to, total } = useMemo(() => {
    const cards = overview?.cards ?? [];
    const starts = cards.map((c) => c.cycleStart);
    const ends = cards.map((c) => c.cycleEnd);
    return {
      from: starts.length > 0 ? formatDate(new Date(Math.min(...starts.map(Date.parse)))) : null,
      to: ends.length > 0 ? formatDate(new Date(Math.max(...ends.map(Date.parse)))) : null,
      total: (overview?.categories ?? []).reduce((sum, c) => sum + c.amount, 0),
    };
  }, [overview]);

  if (isLoading && !overview) {
    return (
      <div className="space-y-2">
        {[0, 1, 2, 3, 4].map((i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
          <div key={i} className="h-10 animate-pulse rounded bg-muted" />
        ))}
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/30 p-8 text-center text-sm text-muted-foreground">
        No categorized card spend for this billing period.
      </div>
    );
  }

  const handleRowClick = (item: CreditCardCategorySpend) => {
    const params = new URLSearchParams({ tab: "transactions" });
    // The synthetic uncategorized bucket (-1) maps to "no category" — the
    // transactions tab has no isUncategorized filter, so just open the window.
    if (!item.isUncategorized) params.set("categoryId", String(item.categoryId));
    if (from) params.set("startDate", from);
    if (to) params.set("endDate", to);
    navigate(`/credit-cards?${params.toString()}`);
  };

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="border-b border-border bg-muted px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-medium">Spend by Category</h2>
            <p className="text-xs text-muted-foreground">categorized spend across all cards</p>
          </div>
          <span
            className="text-xs text-muted-foreground tabular-nums"
            title={`Uncategorized spend is not listed here. Total card spend: ${amountString(overview?.totals.totalSpend ?? 0, "INR", isDemoMode)}`}
          >
            <AmountDisplay amount={total} /> of{" "}
            <AmountDisplay amount={overview?.totals.totalSpend ?? 0} />
          </span>
        </div>
      </div>

      {groups.map((group, index) => {
        const groupTotal = group.items.reduce((s, i) => s + i.amount, 0);
        return (
          <div key={group.label} className={cn(index > 0 && "border-t border-border")}>
            {/* Group section label */}
            <div className="flex items-center justify-between border-b border-border/60 bg-muted/40 px-4 py-1.5">
              <span className="text-xs font-medium uppercase text-muted-foreground">
                {group.label}
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">
                <AmountDisplay amount={groupTotal} />
              </span>
            </div>

            <div className="divide-y divide-border">
              {group.items.map((cat) => {
                const pct = total > 0 ? (cat.amount / total) * 100 : 0;
                return (
                  <button
                    key={cat.categoryId}
                    type="button"
                    onClick={() => handleRowClick(cat)}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-accent/30"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm">{cat.categoryName}</span>
                    <span className="hidden text-xs text-muted-foreground tabular-nums sm:inline">
                      {cat.transactionCount} txn{cat.transactionCount === 1 ? "" : "s"}
                    </span>
                    <span className="text-xs text-muted-foreground/70 tabular-nums">
                      {pct.toFixed(0)}%
                    </span>
                    <span className="w-24 text-right text-sm font-medium tabular-nums">
                      <AmountDisplay amount={cat.amount} />
                    </span>
                    <ChevronRight className="size-4 shrink-0 text-muted-foreground/50" />
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
